import { BasePresenter } from "core/presenter";
import { PaymentRepository, PaymentsReportRepository } from "core/repository";
import Moment from "moment";
import * as FileSaver from "file-saver";
import { AcquirerNames, NetworkNames, SaleStatus } from "core/enum";
import Big from "big.js";
import { Payment } from "core/entity";

class PaymentsReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      emailLoading: false,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day"),
        paymentType: null,
        customType: null,
        in_cash: null
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      revenues: {},
      summary: [],
      results: [],
      customOtherPayments: [],
      order: {
        field: "processed_at",
        direction: "DESC"
      }
    };
  }

  getOrder() {
    const { order } = this.viewModel;
    return order.direction === "DESC" ? `-${order.field}` : order.field;
  }

  async onStart() {
    this.load();
  }

  async load() {
    this.viewModel.loading = true;

    const filters = await this.getFilters();
    await this.loadResults(filters);
    await this.loadSummary(filters);

    this.viewModel.loading = false;
  }

  async onSelectType(type, custom, in_cash) {
    if (type === "total") {
      this.viewModel.filters.customType = null;
      this.viewModel.filters.paymentType = null;
    } else {
      this.viewModel.filters.customType = custom || null;
      this.viewModel.filters.in_cash = type === "credit_card" ? in_cash : null;
      this.viewModel.filters.paymentType = type;
    }
    this.load();
  }

  async loadResults(filters) {
    this.renewCancelToken();
    const repositoryResult = await PaymentsReportRepository.getResultPayment(
      filters
    );

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.payments;

      this.viewModel.pagination = {
        ...this.viewModel.pagination,
        total: repositoryResult.data.pagination.total_items,
        perPage: repositoryResult.data.pagination.per_page,
        page: repositoryResult.data.pagination.page
      };
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar os pagamentos.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async loadSummary(filters) {
    const resultSummary = await PaymentsReportRepository.getSummaryPayment(
      filters
    );

    if (resultSummary.success) {
      this.viewModel.revenues = resultSummary.data.find(
        item => item.payment_type === "total"
      );
      this.viewModel.summary = resultSummary.data;
    } else {
      this.viewModel.summary = [];
      this.viewModel.revenues = {};
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar os pagamentos.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async getFilters() {
    const { filters, pagination } = this.viewModel;
    const { dateFrom, dateTo, paymentType, customType, in_cash } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;

    return {
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      type: paymentType,
      company_uuid,
      page: pagination.page,
      order: this.getOrder(),
      custom: customType,
      in_cash: in_cash
    };
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (this.viewModel.filters[key] !== filters[key]) {
        this.viewModel.filters = { ...this.viewModel.filters, ...filters };
        this.viewModel.pagination.page = 1;
        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }

  async getFiltersEmailCsv() {
    const { dateFrom, dateTo, paymentType } = this.viewModel.filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;

    const onlyUuidsOfOtherPayments = this.viewModel.customOtherPayments
      .map(payment => payment.uuid)
      .join("|");
    const paymentTypes = [
      "all",
      "money",
      "debit_card",
      "credit_card",
      "installments",
      "mp_qr_code",
      "credit_coupon",
      "voucher",
      "others"
    ];
    const filtersEmail = [
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: company_uuid
      }
    ];

    if (dateFrom) {
      filtersEmail.push({
        connector: "AND",
        field: "processed_at",
        operation: "GTE",
        value: dateFrom.startOf("day").toISOString()
      });
    }

    if (dateTo) {
      filtersEmail.push({
        connector: "AND",
        field: "processed_at",
        operation: "LTE",
        value: dateTo.endOf("day").toISOString()
      });
    }

    if (paymentType && paymentType !== "all") {
      if (!paymentTypes.includes(paymentType)) {
        filtersEmail.push({
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "EQ",
          value: paymentType
        });
      } else if (paymentType === "installments") {
        filtersEmail.push({
          connector: "AND",
          field: "type",
          operation: "EQ",
          value: "credit_card"
        });
        filtersEmail.push({
          connector: "AND",
          field: "metadata.installments",
          operation: "GT",
          value: 1
        });
        filtersEmail.push({
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "NIN",
          value: onlyUuidsOfOtherPayments
        });
      } else if (paymentType === "credit_card") {
        filtersEmail.push({
          connector: "AND",
          field: "type",
          operation: "EQ",
          value: "credit_card"
        });
        filtersEmail.push({
          connector: "AND",
          field: "metadata.installments",
          operation: "LTE",
          value: 1
        });
        filtersEmail.push({
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "NIN",
          value: onlyUuidsOfOtherPayments
        });
      } else {
        filtersEmail.push({
          connector: "AND",
          field: "type",
          operation: "EQ",
          value: paymentType
        });
        filtersEmail.push({
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "NIN",
          value: onlyUuidsOfOtherPayments
        });
      }
    }

    return filtersEmail;
  }

  async onSendEmailReport(email) {
    this.viewModel.emailLoading = true;

    const repositoryResult = await PaymentRepository.sendEmailReport({
      email,
      order: [this.viewModel.order],
      filters: await this.getFiltersEmailCsv()
    });

    if (repositoryResult.success) {
      this.sendNotification({
        type: "success",
        content: "Relatório de pagamentos enviado!"
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível enviar o relatório de pagamentos por email.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }

    this.viewModel.emailLoading = false;
  }

  async onDownload(format) {
    this.viewModel.loading = true;

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.viewModel.loading = false;
  }

  async _loadResultsDownload() {
    this.renewCancelToken();
    const filters = await this.getFilters();
    filters.count = 99999;
    const repositoryResult = await PaymentsReportRepository.getResultPayment(
      filters
    );

    if (repositoryResult.success) {
      return repositoryResult.data.payments;
    }
  }

  async _downloadCSV() {
    const content = [
      "Data Processamento; Total (R$); Tipo de Documento; Numero do Documento; Status da Venda; Meio de Pagamento; Parcelas; Bandeira; Credenciadora; NSU"
    ];

    const result = await this._loadResultsDownload();

    result.forEach(payment => {
      const { sale } = payment;
      payment = new Payment(payment);
      content.push(
        `${Moment(payment.processed_at).format("DD/MM/YYYY HH:mm:ss")};${Big(
          payment.getRealAmount()
        )
          .div(100)
          .toFixed(2)};${sale && `${sale.type}`};${sale &&
          `${sale.number}`};${sale &&
          SaleStatus[sale.status]};${payment.friendlyType()};${
          payment.metadata ? payment.metadata.installments : 1
        };${
          payment.metadata ? NetworkNames[payment.metadata.network] || "" : ""
        };${
          payment.metadata
            ? AcquirerNames[payment.metadata.acquirer_name] || ""
            : ""
        };${
          payment.metadata
            ? payment.metadata.acquirer_authorization_number || ""
            : ""
        };`
      );
    });

    const contentBlob = new Blob([content.join("\n")], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "relatorio-de-pagamentos.csv");
  }
}

export default PaymentsReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/payments-report/payments-report.presenter.js